import React, { useEffect, useState } from 'react';
import { Control, Controller } from 'react-hook-form';

import { Button, Checkbox, Input, Slider, cn } from '@design-system';

import { useCustomAmountInput } from '../hooks/use-custom-amount-input';

interface CustomAmountInputProps {
  name: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  updateClient?: (value: number) => void;
  formatValue?: (value: number) => string | number;
  testId?: string;
}

function CustomAmountInput({
  name,
  value,
  min,
  max,
  onChange,
  updateClient,
  formatValue,
  testId,
}: CustomAmountInputProps) {
  const { draft, rangeNotice, handleChange, handleBlur, handleFocus } = useCustomAmountInput({
    value,
    min,
    max,
    onChange,
    updateClient,
  });

  const format = (amount: number) => (formatValue ? String(formatValue(amount)) : String(amount));
  const noticeId = `${name}-custom-input-notice`;
  const noticeMessage =
    rangeNotice === 'below'
      ? `Lowest available amount is ${format(min)}`
      : rangeNotice === 'above'
        ? `Highest available amount is ${format(max)}`
        : null;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-3">
        <label
          htmlFor={`${name}-custom-input`}
          className="text-foreground text-sm font-medium"
        >
          Custom Amount:
        </label>
        <Input
          id={`${name}-custom-input`}
          type="number"
          min={min}
          max={max}
          value={draft}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          className="h-9 w-32"
          placeholder={format(min)}
          aria-describedby={noticeMessage ? noticeId : undefined}
          aria-invalid={noticeMessage ? true : undefined}
          data-testid={`${testId}-custom-input`}
        />
      </div>
      {noticeMessage && (
        <p
          id={noticeId}
          role="status"
          className="text-destructive text-xs"
          data-testid={`${testId}-custom-input-notice`}
        >
          {noticeMessage}
        </p>
      )}
    </div>
  );
}

function SliderTooltip({
  isHovered,
  value,
  position,
  suffix = '',
}: {
  isHovered: boolean;
  value: number | string;
  position: number;
  isDefault?: boolean;
  suffix?: string;
}) {
  return (
    <div
      className={cn(
        'pointer-events-none absolute z-50 -mt-10 whitespace-nowrap rounded-md bg-black px-3 py-1.5 text-xs text-white opacity-0 transition-opacity',
        {
          'opacity-100': isHovered,
        },
      )}
      style={{
        left: `${position}%`,
        transform: `translateX(-${position}%)`,
      }}
    >
      <span className="font-semibold">
        {value}
        {suffix}
      </span>
    </div>
  );
}

interface CustomSliderProps {
  'name': string;
  'control': Control<any>;
  'min': number;
  'max': number;
  'step'?: number;
  'defaultValue': number;
  'formatValue'?: (value: number) => string | number;
  'updateClient'?: (value: number) => void;
  'labelSuffix'?: string;
  'defaultLabel'?: string;
  'hideResetButton'?: boolean;
  /**
   * Pass a function to have the label follow the live drag value; a plain node only
   * updates when the committed form value changes.
   */
  'label'?: React.ReactNode | ((value: number) => React.ReactNode);
  'data-testid'?: string;
  'showCustomInput'?: boolean;
  'customInputLabel'?: string;
  'defaultCustomInputChecked'?: boolean;
  'onCustomInputChange'?: (checked: boolean) => void;
  'showMinMax'?: boolean;
  'minMaxSuffix'?: string;
  'headerActions'?: React.ReactNode;
}

export function CustomSlider({
  name,
  control,
  min,
  max,
  step = 1,
  defaultValue,
  formatValue,
  updateClient,
  labelSuffix = '',
  defaultLabel = 'Default',
  hideResetButton = false,
  label,
  'data-testid': testId,
  showCustomInput = false,
  customInputLabel = 'Enter custom amount',
  defaultCustomInputChecked = false,
  onCustomInputChange,
  showMinMax = false,
  minMaxSuffix = '',
  headerActions,
}: CustomSliderProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [showCustomInputField, setShowCustomInputField] = useState(defaultCustomInputChecked);
  // Held only while the thumb is being dragged. Keeping the in-flight value out of the
  // form means a drag costs nothing beyond this component: no downstream filtering runs
  // until the user lets go.
  const [dragValue, setDragValue] = useState<number | null>(null);

  useEffect(() => {
    setShowCustomInputField(defaultCustomInputChecked);
  }, [defaultCustomInputChecked]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => {
        const displayValue = dragValue ?? value;
        const isDefault = displayValue === defaultValue;
        const resolvedLabel = typeof label === 'function' ? label(displayValue) : label;

        function handleReset(event: React.MouseEvent) {
          event.stopPropagation();
          event.preventDefault();

          setDragValue(null);
          onChange(defaultValue);
          if (updateClient) {
            updateClient(defaultValue);
          }
        }

        return (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              {resolvedLabel && (
                <div className="text-primary-dark text-lg font-semibold">{resolvedLabel}</div>
              )}
              <div className="ml-auto flex items-center gap-2">
                {headerActions}
                {!isDefault && !hideResetButton && (
                  <Button
                    onClick={handleReset}
                    variant="outline"
                    type="button"
                    size="sm"
                    className="h-7 px-2 text-xs"
                  >
                    {defaultLabel}
                  </Button>
                )}
              </div>
            </div>
            <div className={showMinMax ? 'flex items-center gap-3' : 'relative'}>
              {showMinMax && (
                <span className="text-sm text-gray-400">
                  {min}
                  {minMaxSuffix}
                </span>
              )}
              <div className="relative flex-1">
                <Slider
                  value={[displayValue]}
                  onValueChange={newValues => setDragValue(newValues[0])}
                  onValueCommit={newValues => {
                    setDragValue(null);
                    onChange(newValues[0]);
                    if (updateClient) {
                      updateClient(newValues[0]);
                    }
                  }}
                  // Radix skips onValueCommit when a drag ends on its starting value, so
                  // release always clears the local value rather than leaving it stale.
                  onPointerUp={() => setDragValue(null)}
                  min={min}
                  max={max}
                  step={step}
                  className="**:data-[slot=slider-thumb]:size-4 **:data-[slot=slider-track]:h-1.5"
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  data-testid={testId}
                />

                <SliderTooltip
                  isHovered={isHovered}
                  value={formatValue ? formatValue(displayValue) : displayValue}
                  position={((displayValue - min) / (max - min)) * 100}
                  isDefault={isDefault}
                  suffix={labelSuffix}
                />
              </div>
              {showMinMax && (
                <span className="text-sm text-gray-400">
                  {max}
                  {minMaxSuffix}
                </span>
              )}
            </div>
            {showCustomInput && (
              <>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`${name}-show-custom-amount`}
                    checked={showCustomInputField}
                    onCheckedChange={checked => {
                      const val = checked === true;
                      setShowCustomInputField(val);
                      onCustomInputChange?.(val);
                    }}
                  />
                  <label
                    htmlFor={`${name}-show-custom-amount`}
                    className="text-muted-foreground cursor-pointer text-sm"
                  >
                    {customInputLabel}
                  </label>
                </div>
                {showCustomInputField && (
                  <CustomAmountInput
                    name={name}
                    value={displayValue}
                    min={min}
                    max={max}
                    onChange={onChange}
                    updateClient={updateClient}
                    formatValue={formatValue}
                    testId={testId}
                  />
                )}
              </>
            )}
          </div>
        );
      }}
    />
  );
}
